import { z } from "zod";

/**
 * Represents a single address suggestion returned by the geocoding service.
 * Adapts the Siti Data response structure to our internal domain model.
 */
export const AddressSuggestionSchema = z.object({
  id: z
    .string()
    .describe("Unique identifier for the address from the provider"),
  main_text: z
    .string()
    .describe("The primary address line (e.g., '123 Main St')"),
  secondary_text: z
    .string()
    .describe("The secondary address line (e.g., 'New York, NY')"),
  full_address: z
    .string()
    .describe("The complete address formatted as a string"),
  // Optional: Add coordinates if Siti Data returns them in the suggestion list
  // location: z.object({ lat: z.number(), lng: z.number() }).optional(),
});

export type AddressSuggestion = z.infer<typeof AddressSuggestionSchema>;

/**
 * Schema for the geocoding search request.
 */
export const GeocodingRequestSchema = z.object({
  query: z
    .string()
    .min(3, "Query must be at least 3 characters long")
    .describe("The partial address to search for (sent as 'address' to API)"),
  city: z.string().optional().describe("The city to restrict the search to"),
});

export type GeocodingRequest = z.infer<typeof GeocodingRequestSchema>;

/**
 * Schema for the raw response from Google Places Autocomplete API.
 */
export const GoogleMapsPlacePredictionSchema = z.object({
  place_id: z.string(),
  description: z.string(),
  structured_formatting: z.object({
    main_text: z.string(),
    secondary_text: z.string().optional().default(""),
  }),
});

export const GoogleMapsAutocompleteResponseSchema = z.object({
  status: z.string(),
  predictions: z.array(GoogleMapsPlacePredictionSchema).default([]),
});

export type GoogleMapsAutocompleteResponse = z.infer<
  typeof GoogleMapsAutocompleteResponseSchema
>;

export const GoogleMapsPlaceDetailsResponseSchema = z.object({
  status: z.string(),
  result: z.object({
    place_id: z.string(),
    formatted_address: z.string(),
    address_components: z
      .array(
        z.object({
          long_name: z.string(),
          short_name: z.string(),
          types: z.array(z.string()),
        })
      )
      .optional(),
    geometry: z
      .object({ location: z.object({ lat: z.number(), lng: z.number() }) })
      .optional(),
  }),
});

/**
 * Schema for the Geo Data response for a specific country.
 */
export const GeoCitySchema = z.object({
  code: z.string(),
  name: z.string(),
  shipping_score: z.number().nullable().optional(),
});

export const GeoProvinceSchema = z.object({
  code: z.string(),
  name: z.string(),
});

export const GeoDataResponseSchema = z.object({
  country_code: z.string(),
  provinces: z.array(GeoProvinceSchema),
  cities_bundled: z.record(z.string(), z.array(GeoCitySchema)),
  cities_endpoint: z.string().nullable(),
  pickup_points_endpoint: z.string().nullable(),
});

export type GeoDataResponse = z.infer<typeof GeoDataResponseSchema>;
